import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useBundle } from '@/bundle/components/use-bundle';
import { Agreements } from '@/common/components/agreements';
import { Switch, Route } from 'react-router-dom';

import { isVersionBefore } from '@/utils/cmpVersions';
import { trackEvent } from '@/mixpanel';
import { getTrackingName } from '@/product-registry';

import { cva } from 'class-variance-authority';
import { RenewButton } from '@/features/payment/components/renew-button';

import { RemoteContent } from '@/features/cms/components/remote-content';
import styled from 'styled-components';
import useSearchParams from '../../../hooks/useSearchParams';
import MeituanIcon from '../../../common/svgs/MeituanIcon.svg';
import ElemeIcon from '../../../common/svgs/ElemeIcon.svg';
import { useAchievement } from '../hooks/useAchievement';
import { LaunchPaymentButton } from '@/next/components/launch-payment-button';
import { RecallLogic, useAppUserRecallLogic } from '../gql/useAppUserRecallLogic';

import { PlanSelector } from '@/iap/planSelector/plan-selector-ng';
import { PlanNoteNg } from '@/iap/PlanNote';
import { RegularCouponButtonNg } from '@/features/coupon/components/RegularCouponButton';
import { ButtonContent } from '@/iap/purchase-button-default-content';
import { useCoupons } from '@/features/coupon/hooks/use-redeemable-coupons';
import { DisplayPlan } from '@/iap/types';
import { usePlanSelector } from '@/store/plan-slice';
import { cssTrackingOnce } from '@/tracking/css-tracking';
import { useEnrichedFlatValueCoupons } from '@/bnm-common/hooks/useCoupons';
import { isPlanNameIdentical } from '@/bnm-common/util/coupons';

const images = import.meta.glob('./images/*.{png,jpg}', { eager: true, as: 'url' });
const AchievementPageBackground = cva(['block overflow-auto bg-cover']);

const XCD_ONE_MONTH_PLAN_NAME = 'xcd.emcd.p1m.store.chn.v0';
const XCD_001_PLAN_NAME = 'xcd.emcd.recurring.p1m.store.chn.campaign.v0';

export function AchievementPage() {
  const { productName } = useParams<{ productName: string }>();
  const { planName, storeId } = useSearchParams();

  const { data: bundle, isLoading } = useBundle();

  const { data: recallLogic } = useAppUserRecallLogic({
    variables: {
      storeId: storeId ?? '',
      productName,
    },
    enabled: !!storeId,
  });

  /// Jump to IAP or not
  const isIn001 = useMemo(
    () => recallLogic !== RecallLogic.RECALL_CASE_NOT_TARGET_USER && recallLogic !== RecallLogic.RECALL_CASE_NEW_USER,
    [recallLogic]
  );
  const isTrackExpired = useMemo(() => (isIn001 ? bundle?.expired : undefined), [bundle?.expired, isIn001]);

  useEffect(() => {
    if (!import.meta.env.DEV && isVersionBefore({ iOS: '3.9.3', android: '4.0.5', defaultForH5: false })) {
      location.pathname = `/iap/${productName}`;
    }
  }, []);

  useEffect(() => {
    if (isIn001) return;

    if (bundle?.expired) {
      location.pathname = `/iap/${productName}`;
    }
  }, [bundle, isIn001]);

  useEffect(
    function () {
      if (isLoading) return;

      trackEvent('achievement_page_loaded', {
        page_name: 'ACHIEVEMENT_PAGE',
        product_name: getTrackingName(productName),
        expired: isTrackExpired,
        recallLogic,
      });
      cssTrackingOnce(`achievement-${productName}`, 'page_loaded');
    },
    [isTrackExpired, isLoading]
  );

  useEffect(() => {
    if (recallLogic) {
      trackEvent('achievement_page_recallLogic', {
        page_name: 'ACHIEVEMENT_PAGE',
        product_name: getTrackingName(productName),
        recallLogic,
      });
    }
  }, [recallLogic]);

  const { data: coupons = [], isLoading: loadingCoupons } = useEnrichedFlatValueCoupons({
    variables: { status: 'REDEEMABLE' },
    enabled: true,
  });

  const onePennyCoupon = useMemo(() => {
    const onePennyCoupon = coupons
      .filter((coupon) => coupon.tags?.find((t) => t.key === 'upsell' && t.value === 'true'))
      .find((coupon) => coupon.applicablePlans.some((plan) => isPlanNameIdentical(plan, XCD_001_PLAN_NAME)));
    return onePennyCoupon;
  }, [coupons]);

  const currentPlanName = usePlanSelector((state) => state.currentPlan);
  const getCurrentCouponCode = usePlanSelector((state) => state.getCurrentCouponCode);

  const setPlan = usePlanSelector((state) => state.selectPlan);
  const setCoupone = usePlanSelector((state) => state.selectCoupon);

  useEffect(() => {
    if (recallLogic === RecallLogic.RECALL_CASE_001_COUPON && !!onePennyCoupon) {
      setPlan(XCD_001_PLAN_NAME);
      setCoupone(onePennyCoupon);
    }
  }, [recallLogic, onePennyCoupon]);

  return (
    <div className="h-screen h-[100svh] mx-auto grid grid-rows-footer-page pb-safe">
      <Switch>
        <Route>
          <div
            className={AchievementPageBackground()}
            style={{
              backgroundImage: `url(${images['./images/bg.png']})`,
            }}
          >
            <div className="p-4 bg-white m-2 rounded-md">
              <PerformanceData recallLogic={recallLogic} />
            </div>
          </div>
        </Route>
      </Switch>

      <footer className="p-4 text-md border-t border-neutral-400 mb-safe w-full bg-white flex flex-col gap-2 ">
        {recallLogic === RecallLogic.RECALL_CASE_NOT_ELIGIBLE ||
        recallLogic === RecallLogic.RECALL_CASE_SIGN_AND_PAY ? (
          <>
            <Agreements actionName="签约" files={['user-agreement', 'auto-renew-agreement', 'privacy-policy']} />
            <RenewButton
              productName={productName}
              planName={planName}
              children="去开启自动续费"
              trackingContext={{
                expired: isTrackExpired ? 'expired' : 'not_expired',
                recallLogic,
              }}
            />
          </>
        ) : recallLogic === RecallLogic.RECALL_CASE_PACKAGE_SELL ? (
          <>
            <Agreements files={['user-agreement', 'privacy-policy']} />
            <LaunchPaymentButton
              planName={currentPlanName ?? ''}
              couponCode={getCurrentCouponCode() ?? ''}
              trackingContext={{ recallLogic }}
            >
              <ButtonContent />
            </LaunchPaymentButton>
          </>
        ) : recallLogic === RecallLogic.RECALL_CASE_001_COUPON && onePennyCoupon ? (
          <>
            <Agreements actionName="签约" files={['user-agreement', 'auto-renew-agreement', 'privacy-policy']} />
            <RenewButton
              productName={productName}
              planName={XCD_001_PLAN_NAME}
              children="确认协议并开通"
              couponCodeFromParent={getCurrentCouponCode() ?? ''}
              trackingContext={{
                expired: isTrackExpired ? 'expired' : 'not_expired',
                recallLogic,
              }}
            />
          </>
        ) : null}
      </footer>
    </div>
  );
}

const AchievementPagePerformanceData = cva(['flex flex-col justify-around py-8 max-w-md rounded-lg mx-auto relative']);
const AchievementPagePerformanceDataInfo = cva(['top-[200px] flex-shrink mb-4']);
const AchievementPagePerformanceDataInfoHeader = cva(['flex gap-2 justify-center items-baseline mb-4']);

function PerformanceData({ recallLogic }: { recallLogic?: RecallLogic }) {
  return (
    <div className="mx-auto bg-white rounded-lg max-w-lg ">
      <div className={AchievementPagePerformanceData()}>
        <BundleUsageInfo recallLogic={recallLogic} />
      </div>
    </div>
  );
}

function BundleUsageInfo({ recallLogic }: { recallLogic?: RecallLogic }) {
  const { meituanCouponList, elemeCouponList, shouldShowDetail } = useAchievement();

  const { productName } = useParams<{ productName: string }>();

  const filterPlanFn = useCallback(
    (plan: DisplayPlan) =>
      recallLogic === RecallLogic.RECALL_CASE_PACKAGE_SELL ? [XCD_ONE_MONTH_PLAN_NAME].includes(plan.name) : true,
    [recallLogic]
  );

  return (
    <div className={AchievementPagePerformanceDataInfo()}>
      <div className={AchievementPagePerformanceDataInfoHeader()}>
        <h2 className="text-3xl font-bold text-primary">聚合发券宝</h2>
      </div>
      {shouldShowDetail && (
        <div>
          <h3 className="text-1xl font-bold text-primary mb-2 flex items-center">
            <MeituanIcon className="mr-2" style={{ height: 30, width: 30 }} />
            美团发券宝
          </h3>

          <UsageItemList key="meituan-coupon-list" data={meituanCouponList} />
          <h3 className="text-1xl font-bold text-primary mb-2 flex items-center ">
            <ElemeIcon className="mr-2" style={{ height: 30, width: 30 }} />
            饿了么发券宝
          </h3>
          <UsageItemList key="eleme-coupon-list" data={elemeCouponList} />
          <LineBox></LineBox>
          <RenewNote style={{ marginTop: '0.5rem' }}>*订阅后可继续使用，提升店铺流量。</RenewNote>
          <RenewCnt style={{ marginTop: '1rem' }} subCnt={20731} />
        </div>
      )}
      {recallLogic === RecallLogic.RECALL_CASE_PACKAGE_SELL && (
        <div className={shouldShowDetail ? 'mt-6' : 'mb-6'}>
          <PlanSelector productName={productName} plansFilterFn={filterPlanFn} />
          <PlanNoteNg />
          <RegularCouponButtonNg fromPage={`/achievement/${productName}`} />
        </div>
      )}
      {shouldShowDetail === false && (
        <div>
          <RemoteContent contentKey={['iap', 'xcd']} slot="detailImages" />
        </div>
      )}
    </div>
  );
}

interface UsageItem {
  label: string;
  num: number;
  suffix: string;
  description?: string;
}
function UsageItemList({ data, style }: { data: UsageItem[]; style?: React.CSSProperties }) {
  return (
    <UsageItemListContainer style={style}>
      {data.map((usage) => (
        <div key={usage.label} className="usage-data-row">
          <div className="usage-data-main">
            <div className="usage-data-label">{usage.label}</div>
            <div className="usage-data-dash"></div>
            <div className="usage-data-value">{usage.num.toFixed(0) + usage.suffix}</div>
          </div>
          {usage.description && <div className="usage-data-sub">{usage.description}</div>}
        </div>
      ))}
    </UsageItemListContainer>
  );
}

function RenewCnt({ subCnt, style }: { subCnt: number; style?: React.CSSProperties }) {
  return (
    <RenewCntDiv style={style}>
      <div className="flame-box">
        <Flame />
      </div>
      累计
      <span className="strong">{subCnt.toLocaleString()}</span>
      个用户
      <span className="strong">已订阅</span>
    </RenewCntDiv>
  );
}

const UsageItemListContainer = styled.div`
  display: flex;
  flex-direction: column;
  font-size: 1rem;
  row-gap: 0.75rem;
  min-height: 8rem;
  .usage-data-row {
    display: flex;
    flex-direction: column;
    line-height: 20px;
    .usage-data-main {
      display: flex;
      flex-direction: row;
      /* justify-content: space-between; */
      font-weight: 500;
      .usage-data-label {
        max-width: 10rem;
      }
      .usage-data-dash {
        flex: 1;
        border-top: dotted gray;
        margin: 8px 6px;
      }
      .usage-data-value {
        text-align: right;
        min-width: 2rem;
        max-width: 5rem;
      }
    }
    .usage-data-sub {
      font-size: 0.85rem;
      color: #9a9a9a;
    }
  }
`;

const LineBox = styled.div`
  height: 0.5rem;
  border-bottom: dotted 2px #ff8219;
  margin: 0.5rem 1rem;
`;
const RenewNote = styled.div`
  font-size: 0.85rem;
  color: #ff8219;
  text-align: center;
`;
const RenewCntDiv = styled.div`
  position: relative;
  font-size: 0.9rem;
  color: white;
  background-color: #ff8219;
  padding: 0.4rem 6vw;
  border-radius: 99px;
  text-align: center;
  font-size: 0.85rem;
  .flame-box {
    position: absolute;
    bottom: -7px;
    left: -16px;
  }
  .strong {
    font-weight: bold;
    font-size: 4.8vw;
  }
`;

function Flame() {
  const width = Math.floor(73 * 0.7);
  const height = Math.floor(94 * 0.7);

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      width={width}
      height={height}
      //   viewBox="27 699 73 94"
      viewBox={`0 0 ${width} ${height}`}
    >
      <image
        width={width}
        height={height}
        xlinkHref="data:image/png;base64,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"
        data-name="c774376a612cc9ca9cfbfc1dc9c9521cb5f71fa676ac-dDwk61"
        // transform="translate(27 699)"
      />
    </svg>
  );
}
